/* oxlint-disable react/jsx-pascal-case */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';

import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';

import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import { Features } from '../../../../constants/features';
import { emptyArray } from 'ui-components/default-props/empty';
import { Providers, nonDefaultWalletSellerLabelProviderIdsSet } from '../../../../enums/providers';
import { useIsSellerFeatureEnabled } from '../../../../hooks/use-is-seller-feature-enabled';
import { useSellerLabelProviders } from '../../../../hooks/use-seller-label-providers';
import { useWalletedLabelProvidersPreferredOrder } from '../../../../hooks/use-walleted-label-providers-preferred-order';
import { refreshLabelProviders } from '../../../../store/seller/seller.actions';
import { useWallet } from '../../../../wallet/hooks/use-wallet';
import { disableCarrier, enableCarrier } from '../../../../wallet/utilities/api';
import { TH } from '../../providers/providers-list/reuseable-table/components';
import { USD } from '../../../../constants/currency-codes';
import { CarrierRow, HiddenStatus } from './carrier-row';

import messages from '../messages';
import carrierStyles from './carriers-wallet.module.less';

const getProvider = (sellerLabelProviders, walletId_, labelProviderId_) => {
  return sellerLabelProviders.find(({ labelProviderId, walletId }) => labelProviderId_ === labelProviderId && walletId_ === walletId) ?? {};
};
const isCarrierHidden = (sellerLabelProviders, walletId_, labelProviderId_) => {
  const sellerLabelProvider = getProvider(sellerLabelProviders, walletId_, labelProviderId_);
  return sellerLabelProvider?.status === HiddenStatus;
};

export const checkAndUpdateNonDefaultProviders = async (sellerLabelProviders, wallet, updateProviderStatus) => {
  const { isWalletRegistered, currencyCode, walletId } = wallet;
  if (isWalletRegistered || currencyCode !== USD) {
    return Promise.resolve(null);
  }

  const isUspsHidden = isCarrierHidden(sellerLabelProviders, walletId, Providers.USPS.labelProviderId);
  const isUpsHidden = isCarrierHidden(sellerLabelProviders, walletId, Providers.UPSWallet.labelProviderId);

  const updatePromises = Array.from(nonDefaultWalletSellerLabelProviderIdsSet).map((providerId) => {
    const { sellerLabelProviderId } = getProvider(sellerLabelProviders, walletId, providerId);
    const isHidden = isCarrierHidden(sellerLabelProviders, walletId, providerId);
    const shouldHide = isUspsHidden && isUpsHidden && !isHidden && sellerLabelProviderId;
    if (shouldHide) {
      return updateProviderStatus(sellerLabelProviderId, true);
    }
    return Promise.resolve(null);
  });

  return Promise.all(updatePromises);
};

const CarrierTable = ({ walletId = '', eligibleProviderIds = emptyArray, walletSellerLabelProviderIds = emptyArray }) => {
  const dispatch = useDispatch();
  const wallet = useWallet(walletId);

  const updateProviderStatus = useCallback(
    async (sellerLabelProviderId, isActiveCarrier) => {
      const payload = {
        walletId: Number(walletId),
        sellerProviderId: Number(sellerLabelProviderId),
        addon: null,
        insuranceProvider: null,
      };
      const action = isActiveCarrier ? disableCarrier : enableCarrier;
      try {
        return await action(payload);
      } catch {
        return null;
      }
    },
    [walletId],
  );

  const sellerLabelProviders = useSellerLabelProviders();

  const sellerLabelProviderIdsToProviderIds = useMemo(() => {
    return sellerLabelProviders.reduce((map, sellerLabelProvider) => {
      const { sellerLabelProviderId, labelProviderId } = sellerLabelProvider;
      return {
        ...map,
        [sellerLabelProviderId]: labelProviderId,
      };
    }, {});
  }, [sellerLabelProviders]);

  // Hack to disable nonDefault Carriers if UPS and USPS are disabled
  useEffect(() => {
    const hideNonDefaultCarriersIfApplicable = async () => {
      const result = await checkAndUpdateNonDefaultProviders(sellerLabelProviders, wallet, updateProviderStatus);
      const areAllResultsEmpty = Array.isArray(result) && result.every((res) => res === null || res === undefined);
      if (result === null || result === undefined || areAllResultsEmpty) {
        return;
      }
      dispatch(refreshLabelProviders({ shouldRefreshServices: true }));
    };

    hideNonDefaultCarriersIfApplicable();
  }, [sellerLabelProviders, wallet, updateProviderStatus, dispatch]);

  // Note: You can have a provisioned provider (i.e. exists in walletSellerLabelProviderIds) but that same provider may not be setup and will show up in eligibility.
  const providerIdToRowDatum =
    eligibleProviderIds?.reduce((map, providerId) => {
      return {
        ...map,
        [providerId]: { providerId, sellerLabelProviderId: undefined },
      };
    }, {}) || {};

  walletSellerLabelProviderIds.forEach((sellerLabelProviderId) => {
    const providerId = sellerLabelProviderIdsToProviderIds[sellerLabelProviderId];
    if (!providerId) {
      return;
    }
    providerIdToRowDatum[providerId] = { providerId, sellerLabelProviderId };
  });

  const sortedLabelProviders = useWalletedLabelProvidersPreferredOrder(Object.values(providerIdToRowDatum));

  const rowData = sortedLabelProviders.toSorted((a, b) => {
    if (!!a.sellerLabelProviderId && !b.sellerLabelProviderId) {
      return -1;
    }
    if (!a.sellerLabelProviderId && !!b.sellerLabelProviderId) {
      return 1;
    }
    return 0;
  });

  const isSellerFeatureApiV2Enabled = useIsSellerFeatureEnabled(Features.ShipStationApiV2);
  const showCarrierId = isSellerFeatureApiV2Enabled;

  return (
    <table
      className={classnames(carrierStyles['carrier-table'], {
        [carrierStyles['carrier-table-5-columns']]: showCarrierId,
      })}
    >
      <thead>
        <tr>
          <TH key='name' className={carrierStyles['provider-name-cell']}>
            <FormattedMessage {...messages.tableHeaderCarrier} />
          </TH>
          <TH key='services' className={carrierStyles['services-cell']}>
            <FormattedMessage {...messages.tableHeaderServices} />
          </TH>
          <TH key='features' className={carrierStyles['features-cell']}>
            <FormattedMessage {...messages.tableHeaderFeatures} />
          </TH>
          {showCarrierId && (
            <TH key='carrierId' className={carrierStyles['carrier-id-cell']}>
              <span>
                <FormattedMessage {...messages.tableHeaderCarrierId} />
              </span>
              <Tooltip position='bottom'>
                <div className={carrierStyles['header-tooltip']}>
                  <FontAwesomeIcon className={carrierStyles['header-icon']} icon={faExclamationCircle} />
                </div>
                <TooltipContent>
                  <FormattedMessage {...messages.carrierIdTooltip} />
                </TooltipContent>
              </Tooltip>
            </TH>
          )}
          <TH key='actions' className={carrierStyles['actions-cell']}>
            <FormattedMessage {...messages.tableHeaderActions} />
          </TH>
        </tr>
      </thead>
      <tbody>
        {rowData.map(({ sellerLabelProviderId, providerId }) => {
          return (
            <CarrierRow
              key={providerId}
              providerId={providerId}
              sellerLabelProviderId={sellerLabelProviderId}
              showCarrierId={showCarrierId}
              updateProviderStatus={updateProviderStatus}
              walletId={walletId}
            />
          );
        })}
      </tbody>
    </table>
  );
};

CarrierTable.propTypes = {
  walletId: PropTypes.string,
  eligibleProviderIds: PropTypes.array,
  walletSellerLabelProviderIds: PropTypes.array,
};

export default CarrierTable;
